import { computed, effect, inject, Injectable, linkedSignal, type Signal, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { select, Store } from '@ngxs/store';
import { filter, map, take } from 'rxjs';

import { Permissions } from '@efficiently/models';

import { PermissionsService } from '@efficiently/client-api-permissions';
import { ProjectsRepository } from '@efficiently/client-api-projects';
import { PermissionSelectors } from '@efficiently/client-domain-permissions';
import { ProjectDetailSelectors, SetProjectDomainLinkAction } from '@efficiently/client-domain-project-detail';
import { CLARITY_DOMAIN_ROUTE_PARAM } from '@efficiently/client-util-clarity';
import { getParamFromChildRouteSnapshot } from '@efficiently/client-util-helpers';

import { ProjectNavigatorStore } from '@ds/pages/projects/navigator/project-navigator.store';

import { DomainPlaceStore } from '../domain-place/domain-place.store';
import { DOMAIN_PLACE_KIND } from '../domain-place/domain-place-kind.constant';
import type { ProjectDomainLink } from './project-domain-link.model';

type RememberedProjectDomain = {
	readonly domainId: string;
	readonly platformProjectId: string;
};

@Injectable({ providedIn: 'root' })
export class ProjectDomainLinkStore {
	private readonly projectNavigatorStore = inject(ProjectNavigatorStore);
	private readonly domainPlaceStore = inject(DomainPlaceStore);
	private readonly router = inject(Router);
	private readonly store = inject(Store);
	private readonly projectsRepository = inject(ProjectsRepository);

	private readonly loadedProject = select(ProjectDetailSelectors.project);
	private readonly heldPermissions = select(PermissionSelectors.getPermissions);
	private readonly canEditProject = PermissionsService.grants(this.heldPermissions, [Permissions.ProjectEdit]);

	private readonly healedProjectIds = signal<ReadonlySet<string>>(new Set());

	private readonly routeDomainId = toSignal(
		this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			map(() => this.readRouteDomainId()),
		),
		{ initialValue: this.readRouteDomainId() },
	);

	private readonly rememberedProjectDomain = linkedSignal<ProjectDomainLink | null, RememberedProjectDomain | null>({
		source: () => this.linkForDomain(),
		computation: (link, previous) => this.rememberProjectDomain(link, previous?.value ?? null),
	});

	/** The project-domain link of the project page or project domain the reader is on; `null` anywhere else. */
	public readonly current: Signal<ProjectDomainLink | null> = computed(() => {
		const projectId = this.projectNavigatorStore.activeProjectId();

		if (projectId !== null) {
			return this.linkForProject(projectId);
		}

		return this.linkForDomain();
	});

	constructor() {
		effect(() => {
			const projectId = this.projectNavigatorStore.activeProjectId();
			const remembered = this.rememberedProjectDomain();
			const loadedProject = this.loadedProject();
			const canEditProject = this.canEditProject();
			const healedProjectIds = this.healedProjectIds();

			if (projectId === null || remembered === null || remembered.platformProjectId !== projectId) {
				return;
			}

			if (loadedProject?._id !== projectId || (loadedProject.domainId ?? null) !== null) {
				return;
			}

			if (!canEditProject || healedProjectIds.has(projectId)) {
				return;
			}

			untracked(() => this.heal(projectId, remembered.domainId));
		});
	}

	private heal(projectId: string, domainId: string): void {
		this.healedProjectIds.update(ids => new Set(ids).add(projectId));

		this.projectsRepository
			.healProjectDomainLink(domainId)
			.pipe(take(1))
			.subscribe(project => {
				const healedDomainId = project?.domainId ?? '';

				if (project?._id !== projectId || healedDomainId === '') {
					return;
				}

				this.store.dispatch(new SetProjectDomainLinkAction({ projectId, domainId: healedDomainId }));
			});
	}

	private rememberProjectDomain(
		link: ProjectDomainLink | null,
		previous: RememberedProjectDomain | null,
	): RememberedProjectDomain | null {
		if (link === null || link.projectId === null || link.domainId === null) {
			return previous;
		}

		return { domainId: link.domainId, platformProjectId: link.projectId };
	}

	private linkForProject(projectId: string): ProjectDomainLink {
		const loadedProject = this.loadedProject();

		if (loadedProject?._id === projectId) {
			return {
				projectId,
				domainId: loadedProject.domainId ?? null,
				projectName: loadedProject.name,
				isSettled: true,
			};
		}

		return { projectId, domainId: null, projectName: null, isSettled: false };
	}

	private linkForDomain(): ProjectDomainLink | null {
		const place = this.domainPlaceStore.place();
		const domainId = this.routeDomainId();

		if (place === null || place.kind !== DOMAIN_PLACE_KIND.PROJECT || domainId === null) {
			return null;
		}

		return { projectId: place.platformProjectId, domainId, projectName: place.name, isSettled: true };
	}

	private readRouteDomainId(): string | null {
		return getParamFromChildRouteSnapshot<string>(
			this.router.routerState.snapshot.root,
			CLARITY_DOMAIN_ROUTE_PARAM,
		);
	}
}
